import { Link } from 'react-router-dom'
import type { MembershipStatus } from '@/features/poules/api'
import { useTranslation } from '@/i18n'

export function PouleMembershipState({
  status,
  requiresApproval,
  isJoining,
  errorMessage,
  onJoin,
}: {
  status: MembershipStatus
  requiresApproval: boolean
  isJoining: boolean
  errorMessage: string | undefined
  onJoin: () => void
}) {
  const { t } = useTranslation()
  const tone =
    status === 'pending'
      ? 'border-amber-900/50 bg-amber-950/20 text-amber-100'
      : status === 'rejected'
        ? 'border-red-900/50 bg-red-950/20 text-red-100'
        : 'border-[#202427] bg-[#0a0d0f] text-surface-100'

  const title =
    status === 'pending'
      ? t('Je aanvraag wacht nog op akkoord.')
      : status === 'rejected'
        ? t('Je toegang tot deze poule is afgewezen.')
        : t('Doe mee met deze poule.')

  const body =
    status === 'pending'
      ? t('Zodra een beheerder je accepteert, verschijnen hier de dashboard-tab en de rest van de poule.')
      : status === 'rejected'
        ? t('Neem contact op met de poulebeheerder als dit niet klopt of als je opnieuw wilt deelnemen.')
        : requiresApproval
          ? t('Stuur een aanvraag naar de beheerder. Na goedkeuring verschijnen hier de wedstrijden en ranking.')
          : t('Je kunt direct deelnemen. Daarna laden we hier de wedstrijden en ranking van de poule.')

  return (
    <section className={`rounded-[1.35rem] border p-5 ${tone}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-inherit/80">
        {t('Membership')}
      </p>
      <h2 className="mt-3 text-2xl font-black tracking-tight text-inherit">{title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-inherit/80">{body}</p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {status !== 'pending' ? (
          <button
            type="button"
            onClick={onJoin}
            disabled={isJoining}
            className="inline-flex items-center rounded-full bg-accent-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-accent-500 focus-visible:ring-2 focus-visible:ring-accent-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0d0f] disabled:cursor-wait disabled:opacity-60"
          >
            {isJoining ? t('Bezig…') : status === 'rejected' ? t('Opnieuw aanvragen') : requiresApproval ? t('Aanvraag versturen') : t('Deelnemen')}
          </button>
        ) : null}
        <Link
          to="/dashboard/poules"
          className="inline-flex items-center rounded-full border border-current/20 px-4 py-2 text-sm font-semibold text-inherit transition hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0d0f]"
        >
          {t('Terug naar Voetbalpoules')}
        </Link>
      </div>
      {errorMessage ? <p className="mt-3 text-sm text-red-300">{errorMessage}</p> : null}
    </section>
  )
}
